import axiosInstance from '../../../services/axiosInstance';
import { API_V2 } from '../../../models/GWDomain';

const BASE = `${API_V2}/adri`;

export type UiCommandType = 'ui.navigate' | 'ui.highlight' | 'ui.guide';

export interface GuideStep {
  id: string;
  order: number;
  target: string;
  title: string;
  body: string;
  requiredAction?: string;
  nextStepTrigger?: string;
  placement?: string;
  skippable?: boolean;
  optional?: boolean;
}

export interface UiCommand {
  type: UiCommandType;
  requestId: string;
  registryVersion: string;
  route?: string;
  message?: string;
  screenId?: string;
  target?: string;
  timeoutMs?: number;
  guideId?: string;
  milestone?: string;
  routePrefix?: string;
  title?: string;
  steps?: GuideStep[];
  closing?: { text: string; nextMilestone?: string };
}

export interface PendingAction {
  executionId: number;
  tool: string;
  summary: string;
  params: Record<string, unknown>;
  confirmationToken: string;
  expiresAt: string;
}

export interface AdriDownload {
  kind: 'template';
  filename: string;
  base64: string;
}

export type MilestoneStatus = 'done' | 'pending' | 'postponed' | 'skipped';

export interface RouteMilestone {
  id: string;
  title: string;
  hint: string;
  status: MilestoneStatus;
  until?: string;
  guideId?: string;
  available: boolean;
}

export interface RouteStage {
  id: 'operar' | 'vender' | 'equipo' | 'control';
  title: string;
  done: number;
  total: number;
  milestones: RouteMilestone[];
}

export interface RouteState {
  stages: RouteStage[];
  next: { stage: string; milestone: string } | null;
  routeStatus: 'active' | 'silenced' | 'closed';
  reopenAt?: string;
  reopenPending?: boolean;
  nudge: { milestone: string; text: string } | null;
  version: string;
}

export interface AdriStatus {
  copilot: boolean;
  reason: 'KILL_SWITCH' | 'NOT_IN_PILOT' | 'COMPANY_DISABLED' | 'NO_API_KEY' | null;
  toolsDisabled: string[];
  registryVersion: string;
}

export interface ConfirmResult {
  executionId: number;
  decision: string;
  result?: Record<string, unknown>;
  message?: string;
}

export const getAdriStatus = async (): Promise<AdriStatus> => {
  const { data } = await axiosInstance.get(`${BASE}/status`);
  return data.data ?? data;
};

export const getRoute = async (): Promise<RouteState> => {
  const { data } = await axiosInstance.get(`${BASE}/route`);
  return data.data;
};

/** Spec 052 (US5): saludo de traspaso desde la bienvenida; null si no aplica. */
export const getHandoff = async (): Promise<{ greeting: string | null }> => {
  const { data } = await axiosInstance.get(`${BASE}/handoff`);
  return data.data ?? { greeting: null };
};

export const getGuide = async (guideId: string): Promise<UiCommand> => {
  const { data } = await axiosInstance.get(`${BASE}/route/guide/${encodeURIComponent(guideId)}`);
  return data.data;
};

export const postponeMilestone = async (milestone: string): Promise<RouteState> => {
  const { data } = await axiosInstance.post(
    `${BASE}/route/${encodeURIComponent(milestone)}/postpone`,
    {},
  );
  return data.data;
};

export const dismissMilestone = async (milestone: string): Promise<RouteState> => {
  const { data } = await axiosInstance.post(
    `${BASE}/route/${encodeURIComponent(milestone)}/dismiss`,
    {},
  );
  return data.data;
};

export const dismissAll = async (): Promise<RouteState> => {
  const { data } = await axiosInstance.post(`${BASE}/route/dismiss-all`, {});
  return data.data;
};

export const nudgeShown = async (milestone: string): Promise<{ accepted: boolean }> => {
  const { data } = await axiosInstance.post(`${BASE}/route/nudge-shown`, { milestone });
  return data.data;
};

export const confirmExecution = async (
  executionId: number,
  confirmationToken: string,
): Promise<ConfirmResult> => {
  const { data } = await axiosInstance.post(`${BASE}/executions/${executionId}/confirm`, {
    confirmationToken,
  });
  return data.data;
};

export const cancelExecution = async (executionId: number): Promise<{ ok: boolean }> => {
  const { data } = await axiosInstance.post(`${BASE}/executions/${executionId}/cancel`, {});
  return data.data;
};
